import { __rest } from "tslib";
import { useCallback, useMemo } from 'react';
import { useFlightsMinPricesInPeriodRTQuery } from '@websky/graphql';
import { isRequireCurrencyParam } from '../../../../utils';
import { getCurrency } from '../../../../cache';
import { convertAviaToMinPricesParameters } from '@shared/lib/results/convertAviaToMinPricesParameters';
export const useMinPricesInPeriod = (options) => {
    var _a;
    const optionsClone = Object.assign(Object.assign({}, options), { variables: Object.assign(Object.assign({}, options.variables), { parameters: convertAviaToMinPricesParameters(Object.assign({}, options.variables.parameters)) }) });
    if (isRequireCurrencyParam()) {
        optionsClone.variables.parameters.currency = getCurrency();
    }
    const _b = useFlightsMinPricesInPeriodRTQuery(optionsClone), { data, loading } = _b, props = __rest(_b, ["data", "loading"]);
    const pricesByLeg = useMemo(() => {
        var _a;
        const pricesMap = new Map();
        const legs = (_a = data === null || data === void 0 ? void 0 : data.FlightsMinPricesInPeriodRT) === null || _a === void 0 ? void 0 : _a.datesWithLowestPricesByLegs;
        if (!legs) {
            return pricesMap;
        }
        const processDates = (targetMap, dates) => {
            var _a, _b;
            if (!(dates === null || dates === void 0 ? void 0 : dates.length)) {
                return false;
            }
            let hasPrices = false;
            for (const dateWithPrice of dates) {
                const datePriceAmount = (_a = dateWithPrice.price) === null || _a === void 0 ? void 0 : _a.amount;
                const existDate = targetMap.get(dateWithPrice.date);
                const existPriceAmount = (_b = existDate === null || existDate === void 0 ? void 0 : existDate.price) === null || _b === void 0 ? void 0 : _b.amount;
                if (!(existDate === null || existDate === void 0 ? void 0 : existDate.price) || datePriceAmount < existPriceAmount) {
                    targetMap.set(dateWithPrice.date, dateWithPrice);
                }
                if (dateWithPrice === null || dateWithPrice === void 0 ? void 0 : dateWithPrice.price) {
                    hasPrices = true;
                }
            }
            return hasPrices;
        };
        legs.forEach(leg => {
            const legId = +leg.legId;
            let pricesLeg = pricesMap.get(legId);
            if (!pricesLeg) {
                pricesLeg = { datesOW: new Map(), datesRT: new Map(), hasOWPrices: false, hasRTPrices: false };
                pricesMap.set(legId, pricesLeg);
            }
            pricesLeg.hasOWPrices = processDates(pricesLeg.datesOW, leg.oneWay);
            pricesLeg.hasRTPrices = processDates(pricesLeg.datesRT, leg.halfRoundTrip);
        });
        return pricesMap;
    }, [(_a = data === null || data === void 0 ? void 0 : data.FlightsMinPricesInPeriodRT) === null || _a === void 0 ? void 0 : _a.datesWithLowestPricesByLegs]);
    const getPrices = useCallback((legId, isRT) => {
        var _a;
        const legsCount = pricesByLeg.size;
        if (!legsCount) {
            return [];
        }
        const prices = (_a = pricesByLeg.get(legId)) !== null && _a !== void 0 ? _a : [...pricesByLeg.values()][0];
        if (!prices) {
            return [];
        }
        // OW min prices
        if (prices.hasOWPrices && !prices.hasRTPrices) {
            return [...prices.datesOW.values()];
        }
        // RT min prices
        if (prices.hasRTPrices && !prices.hasOWPrices) {
            return [...prices.datesRT.values()];
        }
        // Combined RT and OW min prices for RT
        const isActualRT = isRT && legsCount > 1;
        if (isActualRT && prices.hasRTPrices && prices.hasOWPrices) {
            return [...prices.datesOW.entries()].map(([date, owDateWithPrice]) => {
                const rtDateWithPrice = prices.datesRT.get(date);
                return !!(rtDateWithPrice === null || rtDateWithPrice === void 0 ? void 0 : rtDateWithPrice.price) ? rtDateWithPrice : owDateWithPrice;
            });
        }
        return [...prices.datesOW.values()];
    }, [pricesByLeg]);
    return Object.assign({ data,
        pricesByLeg, hasPrices: pricesByLeg.size > 0, loading,
        getPrices }, props);
};
